import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import React, { cloneElement, useCallback } from 'react';
import Transition, { ENTERED, ENTERING, EXITED, EXITING } from 'react-transition-group/Transition';
import transitionEndListener from '../transitionEndListener';
import triggerBrowserReflow from '../triggerBrowserReflow';
const MARGINS = {
    height: [
        'marginTop',
        'marginBottom'
    ],
    width: [
        'marginLeft',
        'marginRight'
    ]
};
function capitalize(string) {
    return `${string.charAt(0).toUpperCase()}${string.slice(1)}`;
}
function getDimensionVal(dimension, elem) {
    const value = elem[`offset${capitalize(dimension)}`];
    const margins = MARGINS[dimension];
    return value + (parseInt(elem.style[margins[0]], 10) || 0) + (parseInt(elem.style[margins[1]], 10) || 0);
}
const _getScrollDimensionValue = (elem, dimension)=>{
    return `${elem[`scroll${capitalize(dimension)}`]}px`;
};
const collapseStyles = {
    [EXITED]: 'collapse',
    [EXITING]: 'collapsing',
    [ENTERING]: 'collapsing',
    [ENTERED]: 'collapse show'
};
/**
 * Use the Collapse component to animate an element collapsing or expanding.
 */ const CollapseFn = ({ mountOnEnter = false, unmountOnExit = false, timeout = 300, onEnter, onEntering, onEntered, onExit, onExiting, onExited, appear = false, dimension = 'height', getDimensionValue = getDimensionVal, role, children, nodeRef: nodeRefProps, ...rest }, transitionRef)=>{
    const hasExternalNodeRef = nodeRefProps !== undefined;
    const internalNodeRef = React.useRef(null);
    // Om en nodeRef skickas in använder vi den, annars skapar vi en egen
    const actualRef = hasExternalNodeRef ? nodeRefProps : internalNodeRef;
    const dim = typeof dimension === 'function' ? dimension() : dimension;
    const normalizeEventHandler = useCallback((e)=>{
        if (hasExternalNodeRef) {
            return (...params)=>e(...params);
        }
        return (...params)=>e(actualRef.current, ...params);
    }, [
        hasExternalNodeRef
    ]);
    const handleEnter = useCallback((isAppearing)=>{
        actualRef.current.style[dim] = '0';
        onEnter && normalizeEventHandler(onEnter)(isAppearing);
    }, [
        dim,
        onEnter,
        actualRef
    ]);
    const handleEntering = useCallback((isAppearing)=>{
        actualRef.current.style[dim] = _getScrollDimensionValue(actualRef.current, dim);
        onEntering && normalizeEventHandler(onEntering)(isAppearing);
    }, [
        dim,
        onEntering
    ]);
    const handleEntered = useCallback((isAppearing)=>{
        actualRef.current.style[dim] = '';
        onEntered && normalizeEventHandler(onEntered)(isAppearing);
    }, [
        dim,
        onEntered
    ]);
    const handleExit = useCallback(()=>{
        actualRef.current.style[dim] = `${getDimensionValue(dim, actualRef.current)}px`;
        triggerBrowserReflow(actualRef.current);
        onExit && normalizeEventHandler(onExit)();
    }, [
        dim,
        getDimensionValue,
        onExit
    ]);
    const handleExiting = useCallback(()=>{
        actualRef.current.style[dim] = '';
        onExiting && normalizeEventHandler(onExiting)();
    }, [
        dim,
        onExiting
    ]);
    const handleExited = useCallback(()=>{
        onExited && normalizeEventHandler(onExited)();
    }, [
        onExited
    ]);
    const endListener = useCallback((handler)=>{
        transitionEndListener(actualRef.current, handler);
    }, []);
    return /*#__PURE__*/ _jsx(Transition, {
        addEndListener: endListener,
        mountOnEnter: mountOnEnter,
        unmountOnExit: unmountOnExit,
        appear: appear,
        timeout: timeout,
        role: role,
        "aria-expanded": role ? rest.in : null,
        onEnter: handleEnter,
        onEntering: handleEntering,
        onEntered: handleEntered,
        onExit: handleExit,
        onExiting: handleExiting,
        onExited: handleExited,
        nodeRef: actualRef,
        ref: transitionRef,
        ...rest,
        children: (state)=>/*#__PURE__*/ cloneElement(children, {
                className: classNames(children.props.className, collapseStyles[state], dim === 'width' && 'collapse-horizontal'),
                // Om vi får in en nodeRef får vi anta att den redan är inskickad till children
                ...hasExternalNodeRef ? {} : {
                    ref: actualRef
                }
            })
    });
};
// TODO: Trixande för korrekta typer med forwardRef och generisk komponent. Gör om när vi kör react 19.
export const Collapse = /*#__PURE__*/ React.forwardRef(CollapseFn);
Collapse.displayName = 'Collapse';
